import type { MapDocument, MapStand, MapArtworkLabel } from "../shared/types";
import { VENUE_MAP_ARTWORK_LABEL_HEIGHT } from "./venueMapArtwork";
import { mapPreviewOpacity } from "../shared/mapPresentation";
import { mapStandHeight } from "./venueMapProjection";
import { mapStandColors, mapStandSaturation } from "./venueMapAppearance";
import { venueMapLogoLayout } from "./venueMapLogoLayout";
import {
  venueMapBoothFallbackText,
  venueMapBoothLabelRotation,
  venueMapBoothTextLayout,
} from "./venueMapBoothLabelLayout";
import type { VenueMapLogoAsset, VenueMapLogos } from "./useVenueMapLogos";
import {
  venueMapWebGLColor,
  type VenueMapVertexRange,
} from "./venueMapWebGLGeometry";

export interface VenueMapLabelBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Conservative bounds: Bezier control hulls and complete rotated arc ellipses. */
export function venueMapLabelPathBounds(path: string): VenueMapLabelBounds {
  if (!path.trim()) throw new Error("Map label outline is empty");
  const invalid = (): never => {
    throw new Error("Invalid map label outline");
  };
  // A sticky scanner rejects unsupported characters instead of silently dropping
  // them. Each iteration consumes a token or fails, including after close-path.
  const token =
    /[MmLlHhVvCcSsQqTtAaZz]|[-+]?(?:\d*\.\d+|\d+\.?\d*)(?:[eE][-+]?\d+)?/y;
  const tokens: string[] = [];
  for (let position = 0; position < path.length;) {
    if (/[ \t\r\n,]/.test(path[position])) {
      position++;
      continue;
    }
    token.lastIndex = position;
    const match = token.exec(path);
    if (!match) invalid();
    tokens.push(match![0]);
    position = token.lastIndex;
  }
  if (!/^[Mm]$/.test(tokens[0] || "")) invalid();
  const bounds = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  };
  let x = 0;
  let y = 0;
  let startX = 0;
  let startY = 0;
  let index = 0;
  let command = "";
  let cubicControl: [number, number] | undefined;
  let quadraticControl: [number, number] | undefined;
  const include = (px: number, py: number) => {
    if (!Number.isFinite(px) || !Number.isFinite(py)) invalid();
    bounds.minX = Math.min(bounds.minX, px);
    bounds.minY = Math.min(bounds.minY, py);
    bounds.maxX = Math.max(bounds.maxX, px);
    bounds.maxY = Math.max(bounds.maxY, py);
  };
  const number = () => {
    const part = tokens[index++];
    const value = Number(part);
    if (part === undefined || !Number.isFinite(value)) invalid();
    return value;
  };
  const flag = () => {
    const part = tokens[index++];
    if (part !== "0" && part !== "1") invalid();
    return part === "1";
  };
  while (index < tokens.length) {
    if (/^[A-Za-z]$/.test(tokens[index])) command = tokens[index++];
    const operation = command.toLowerCase();
    const relative = command !== command.toUpperCase();
    const point = (): [number, number] => [
      number() + (relative ? x : 0),
      number() + (relative ? y : 0),
    ];
    let nextCubic: [number, number] | undefined;
    let nextQuadratic: [number, number] | undefined;
    switch (operation) {
      case "m":
        [x, y] = point();
        startX = x;
        startY = y;
        command = relative ? "l" : "L";
        break;
      case "l":
        [x, y] = point();
        break;
      case "h":
        x = number() + (relative ? x : 0);
        break;
      case "v":
        y = number() + (relative ? y : 0);
        break;
      case "c": {
        const firstControl = point();
        nextCubic = point();
        include(...firstControl);
        include(...nextCubic);
        [x, y] = point();
        break;
      }
      case "s": {
        const firstControl: [number, number] = cubicControl
          ? [2 * x - cubicControl[0], 2 * y - cubicControl[1]]
          : [x, y];
        nextCubic = point();
        include(...firstControl);
        include(...nextCubic);
        [x, y] = point();
        break;
      }
      case "q":
        nextQuadratic = point();
        include(...nextQuadratic);
        [x, y] = point();
        break;
      case "t":
        nextQuadratic = quadraticControl
          ? [2 * x - quadraticControl[0], 2 * y - quadraticControl[1]]
          : [x, y];
        include(...nextQuadratic);
        [x, y] = point();
        break;
      case "a": {
        let radiusX = number();
        let radiusY = number();
        const angle = ((number() % 360) * Math.PI) / 180;
        const largeArc = flag();
        const sweep = flag();
        const [endX, endY] = point();
        if (radiusX < 0 || radiusY < 0) invalid();
        // SVG omits coincident-endpoint arcs and treats zero radii as lines.
        if (radiusX > 0 && radiusY > 0 && (x !== endX || y !== endY)) {
          const cosine = Math.cos(angle);
          const sine = Math.sin(angle);
          const halfX = (x - endX) / 2;
          const halfY = (y - endY) / 2;
          const localX = cosine * halfX + sine * halfY;
          const localY = -sine * halfX + cosine * halfY;
          // Equivalent to SVG's radius correction without squaring very small
          // radii. Both corrected radii retain the ellipse's aspect ratio.
          const minimumX = Math.hypot(
            localX,
            localY === 0 ? 0 : localY * (radiusX / radiusY),
          );
          const minimumY = Math.hypot(
            localX === 0 ? 0 : localX * (radiusY / radiusX),
            localY,
          );
          radiusX = Math.max(radiusX, minimumX);
          radiusY = Math.max(radiusY, minimumY);
          const normalizedX = localX / radiusX;
          const normalizedY = localY / radiusY;
          const distance = Math.hypot(normalizedX, normalizedY);
          const extentX = Math.hypot(radiusX * cosine, radiusY * sine);
          const extentY = Math.hypot(radiusX * sine, radiusY * cosine);
          let centerX = (x + endX) / 2;
          let centerY = (y + endY) / 2;
          if (distance > 0) {
            const sign = largeArc === sweep ? -1 : 1;
            const factor = sign * Math.sqrt(Math.max(0, 1 - distance ** 2));
            const localCenterX = radiusX * (normalizedY / distance) * factor;
            const localCenterY = -radiusY * (normalizedX / distance) * factor;
            centerX += cosine * localCenterX - sine * localCenterY;
            centerY += sine * localCenterX + cosine * localCenterY;
            include(centerX - extentX, centerY - extentY);
            include(centerX + extentX, centerY + extentY);
          } else {
            // At subnormal precision the center direction can disappear. An
            // endpoint plus twice the extents still contains every possible arc.
            include(centerX - 2 * extentX, centerY - 2 * extentY);
            include(centerX + 2 * extentX, centerY + 2 * extentY);
          }
        }
        x = endX;
        y = endY;
        break;
      }
      case "z":
        x = startX;
        y = startY;
        command = "";
        break;
      default:
        invalid();
    }
    include(x, y);
    cubicControl = nextCubic;
    quadraticControl = nextQuadratic;
  }
  return bounds;
}

export interface VenueMapTextRun {
  text: string;
  x: number;
  y: number;
  size: number;
  weight: number;
  align: CanvasTextAlign;
  rotation?: number;
}

function configureText(
  context: CanvasRenderingContext2D,
  run: VenueMapTextRun,
) {
  context.font = `${run.weight} ${run.size}px Arial, sans-serif`;
  context.textAlign = run.align;
  context.textBaseline = "alphabetic";
}

/** Keep edited/new labels in the same atlas as the authored outlines. */
export function venueMapTextBounds(
  context: CanvasRenderingContext2D,
  runs: VenueMapTextRun[],
): VenueMapLabelBounds {
  const bounds = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  };
  for (const run of runs) {
    configureText(context, run);
    const metric = context.measureText(run.text);
    const left =
      metric.actualBoundingBoxLeft ??
      (run.align === "center"
        ? metric.width / 2
        : run.align === "right" || run.align === "end"
          ? metric.width
          : 0);
    const right =
      metric.actualBoundingBoxRight ??
      (run.align === "center"
        ? metric.width / 2
        : run.align === "right" || run.align === "end"
          ? 0
          : metric.width);
    const top = -(metric.actualBoundingBoxAscent ?? run.size);
    const bottom = metric.actualBoundingBoxDescent ?? run.size * 0.25;
    const radians = ((run.rotation ?? 0) * Math.PI) / 180;
    for (const [x, y] of [
      [-left, top],
      [right, top],
      [right, bottom],
      [-left, bottom],
    ]) {
      const px = run.x + x * Math.cos(radians) - y * Math.sin(radians);
      const py = run.y + x * Math.sin(radians) + y * Math.cos(radians);
      bounds.minX = Math.min(bounds.minX, px);
      bounds.maxX = Math.max(bounds.maxX, px);
      bounds.minY = Math.min(bounds.minY, py);
      bounds.maxY = Math.max(bounds.maxY, py);
    }
  }
  return runs.length ? bounds : { minX: 0, minY: 0, maxX: 1, maxY: 1 };
}

export function paintVenueMapText(
  context: CanvasRenderingContext2D,
  runs: VenueMapTextRun[],
  stroke = false,
) {
  for (const run of runs) {
    configureText(context, run);
    if (run.rotation) {
      context.save();
      context.translate(run.x, run.y);
      context.rotate((run.rotation * Math.PI) / 180);
      if (stroke) context.strokeText(run.text, 0, 0);
      else context.fillText(run.text, 0, 0);
      context.restore();
    } else if (stroke) context.strokeText(run.text, run.x, run.y);
    else context.fillText(run.text, run.x, run.y);
  }
}

type Label = {
  ground?: MapArtworkLabel;
  stand: MapStand;
  path?: string;
  logo?: VenueMapLogoAsset;
  text: VenueMapTextRun[];
  bounds: VenueMapLabelBounds;
};
type PackedLabel = Label & {
  x: number;
  y: number;
  width: number;
  height: number;
  left: number;
  top: number;
};

const atlasPadding = 8;
const nextPowerOfTwo = (value: number) =>
  2 ** Math.ceil(Math.log2(Math.max(1, value)));

function packLabels(labels: Label[], scale: number, limit: number) {
  const tiles = labels
    .map((label) => {
      const left = Math.floor(label.bounds.minX * scale) - atlasPadding;
      const top = Math.floor(label.bounds.minY * scale) - atlasPadding;
      return {
        ...label,
        x: 0,
        y: 0,
        left,
        top,
        width: Math.ceil(label.bounds.maxX * scale) + atlasPadding - left,
        height: Math.ceil(label.bounds.maxY * scale) + atlasPadding - top,
      };
    })
    .sort((a, b) => b.height - a.height || b.width - a.width);
  const minimumWidth = nextPowerOfTwo(
    Math.max(1, ...tiles.map((tile) => tile.width)),
  );
  let best:
    { width: number; height: number; labels: PackedLabel[] } | undefined;
  for (let width = minimumWidth; width <= limit; width *= 2) {
    let x = 0;
    let y = 0;
    let shelfHeight = 0;
    const packed = tiles.map((tile) => {
      if (x + tile.width > width) {
        x = 0;
        y += shelfHeight;
        shelfHeight = 0;
      }
      const item = { ...tile, x, y };
      x += tile.width;
      shelfHeight = Math.max(shelfHeight, tile.height);
      return item;
    });
    const height = nextPowerOfTwo(y + shelfHeight);
    if (
      height <= limit &&
      (!best ||
        width * height < best.width * best.height ||
        (width * height === best.width * best.height &&
          Math.max(width, height) < Math.max(best.width, best.height)))
    ) {
      best = { width, height, labels: packed };
    }
  }
  return best;
}

export interface VenueMapWebGLLabelAtlas {
  canvas: HTMLCanvasElement;
  vertices: Float32Array;
  /** Prefix rendered on the floor, before booths and navigation lines. */
  groundVertexCount: number;
  /** Float offsets into the reusable vertex array for each atlas tile. */
  ranges: { standId: string; start: number; end: number; logo?: true }[];
}

/** Route emphasis changes geometry, never glyphs or texture coordinates. */
export function updateVenueMapWebGLLabelVertices(
  atlas: Pick<VenueMapWebGLLabelAtlas, "vertices" | "ranges">,
  map: MapDocument,
): VenueMapVertexRange[] {
  const changed: VenueMapVertexRange[] = [];
  const stands = new Map(map.geometry.stands.map((stand) => [stand.id, stand]));
  for (const range of atlas.ranges) {
    const stand = stands.get(range.standId);
    if (!stand) continue;
    const height = Math.max(0.025, mapStandHeight(stand)) + 0.065;
    const opacity = mapPreviewOpacity(stand);
    const ink = range.logo
      ? [-1 - mapStandSaturation(stand), 0, 0]
      : venueMapWebGLColor(mapStandColors(stand).ink);
    if (
      atlas.vertices[range.start + 2] === Math.fround(height) &&
      atlas.vertices[range.start + 5] === Math.fround(ink[0]) &&
      atlas.vertices[range.start + 6] === Math.fround(ink[1]) &&
      atlas.vertices[range.start + 7] === Math.fround(ink[2]) &&
      atlas.vertices[range.start + 8] === Math.fround(opacity)
    )
      continue;
    changed.push({ start: range.start, end: range.end });
    for (let offset = range.start; offset < range.end; offset += 9) {
      atlas.vertices[offset + 2] = height;
      atlas.vertices[offset + 5] = ink[0];
      atlas.vertices[offset + 6] = ink[1];
      atlas.vertices[offset + 7] = ink[2];
      atlas.vertices[offset + 8] = opacity;
    }
  }
  return changed;
}

/** Build per label/logo revision, never per camera or route change. Pixel storage stays bounded independently of zoom. */
export function buildVenueMapWebGLLabelAtlas(
  map: MapDocument,
  maxTextureSize: number,
  logos?: VenueMapLogos,
): VenueMapWebGLLabelAtlas {
  if (!Number.isFinite(maxTextureSize) || maxTextureSize < 64) {
    throw new Error("Map label texture size is unsupported");
  }
  const limit = 2 ** Math.floor(Math.log2(Math.min(2048, maxTextureSize)));
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Map label texture could not be created");
  const labels = map.geometry.stands
    .filter((stand) => !stand.hidden)
    .flatMap((stand): Label[] => {
      const asset = stand.logo ? logos?.get(stand.logo.id) : undefined;
      const layout = asset ? venueMapLogoLayout(stand) : undefined;
      // Keep the corner number separate from the name. This compact form is
      // also the fallback when padding many individual lines costs more space.
      const textTiles = (text: VenueMapTextRun[]): Label[] =>
        [text.slice(0, 1), text.slice(1)]
          .filter((runs) => runs.length > 0)
          .map((runs) => ({
            stand,
            text: runs,
            bounds: venueMapTextBounds(context, runs),
          }));
      if (asset && layout) {
        return [
          ...textTiles(layout.text),
          {
            stand,
            logo: asset,
            text: [],
            bounds: {
              minX: layout.logo.x,
              minY: layout.logo.y,
              maxX: layout.logo.x + layout.logo.width,
              maxY: layout.logo.y + layout.logo.height,
            },
          },
        ];
      }
      const textLayout = venueMapBoothTextLayout(stand);
      if (textLayout) return textTiles(textLayout.text);
      const path =
        stand.mapLabel?.trim() ||
        stand.label.showId === false ||
        stand.labelRotation
          ? undefined
          : map.labels.stands[stand.id];
      const text = venueMapBoothFallbackText(stand);
      return [
        {
          stand,
          path,
          text,
          bounds: path
            ? venueMapLabelPathBounds(path)
            : venueMapTextBounds(context, text),
        },
      ];
    });
  for (const ground of map.geometry.artworkLabels ?? []) {
    const text: VenueMapTextRun[] = [
      {
        text: ground.text,
        x: 0,
        y: 0,
        size: ground.fontSize,
        weight: 600,
        align: "left",
      },
    ];
    // A private atlas-only anchor reuses the compact booth glyph packing; it
    // is not a selectable booth, route obstacle, or independently allocated texture.
    labels.push({
      ground,
      stand: {
        id: "",
        name: "",
        shortName: "",
        category: "facility",
        height: 0,
        polygons: [],
        label: {
          x: ground.x,
          y: ground.y,
          rotation: ground.rotation ?? 0,
          showId: false,
          idFontSize: 1,
          idY: 0,
          nameFontSize: ground.fontSize,
          nameLines: [],
        },
      },
      text,
      bounds: venueMapTextBounds(context, text),
    });
  }
  // Split name lines so gaps and the blank sides of shorter lines do not
  // consume source detail. Very dense maps can instead use the compact tiles
  // above: per-line padding must never make a previously supported map fail.
  const lineLabels = labels.flatMap((label) =>
    label.path || label.logo || label.text.length < 2
      ? [label]
      : label.text.map((run) => ({
          ...label,
          text: [run],
          bounds: venueMapTextBounds(context, [run]),
        })),
  );
  const linePaddingFits =
    lineLabels.length * (atlasPadding * 2) ** 2 <= limit ** 2;
  const pack = (scale: number) =>
    (linePaddingFits ? packLabels(lineLabels, scale, limit) : undefined) ||
    packLabels(labels, scale, limit);
  // Reserve more source detail for zoomed views without raising the texture
  // cap or rebuilding glyphs during gestures. After the two sharper tiers,
  // retain the original density ladder and minimum for heavily populated maps.
  let scale = 8;
  let packed = pack(scale);
  while (!packed && scale > 0.25) {
    scale = scale > 4 ? scale - 2 : scale * 0.75;
    packed = pack(scale);
  }
  if (!packed) throw new Error("Map labels exceed the supported texture size");

  canvas.width = packed.width;
  canvas.height = packed.height;
  context.fillStyle = "#fff";
  const vertices: number[] = [];
  const ranges: VenueMapWebGLLabelAtlas["ranges"] = [];
  let groundVertexCount = 0;
  // Packing positions stay unchanged. A contiguous floor prefix lets the GPU
  // draw source text below booths/routes while sharing their single texture.
  const orderedLabels = [
    ...packed.labels.filter((tile) => tile.ground),
    ...packed.labels.filter((tile) => !tile.ground),
  ];
  for (const tile of orderedLabels) {
    context.setTransform(
      scale,
      0,
      0,
      scale,
      tile.x - tile.left,
      tile.y - tile.top,
    );
    if (tile.logo) {
      context.drawImage(
        tile.logo.image,
        tile.bounds.minX,
        tile.bounds.minY,
        tile.bounds.maxX - tile.bounds.minX,
        tile.bounds.maxY - tile.bounds.minY,
      );
    } else if (tile.path) context.fill(new Path2D(tile.path));
    else paintVenueMapText(context, tile.text);
    const angle = (venueMapBoothLabelRotation(tile.stand) * Math.PI) / 180;
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    const height = tile.ground
      ? VENUE_MAP_ARTWORK_LABEL_HEIGHT
      : Math.max(0.025, mapStandHeight(tile.stand)) + 0.065;
    const palette = mapStandColors(tile.stand);
    // Negative red marks a full-colour logo and carries its current saturation.
    // Text tiles retain their ordinary positive ink; both reuse the same atlas.
    const ink = tile.logo
      ? [-1 - mapStandSaturation(tile.stand), 0, 0]
      : venueMapWebGLColor(tile.ground?.color ?? palette.ink);
    const corners = [
      [0, 0],
      [tile.width, 0],
      [tile.width, tile.height],
      [0, tile.height],
    ];
    const start = vertices.length;
    for (const corner of [0, 1, 2, 0, 2, 3]) {
      const [dx, dy] = corners[corner];
      const x = (tile.left + dx) / scale;
      const y = (tile.top + dy) / scale;
      vertices.push(
        tile.stand.label.x + x * cosine - y * sine,
        tile.stand.label.y + x * sine + y * cosine,
        height,
        (tile.x + dx) / canvas.width,
        (tile.y + dy) / canvas.height,
        ink[0],
        ink[1],
        ink[2],
        mapPreviewOpacity(tile.stand),
      );
    }
    if (tile.ground) groundVertexCount += 6;
    else
      ranges.push({
        standId: tile.stand.id,
        start,
        end: vertices.length,
        ...(tile.logo ? { logo: true as const } : {}),
      });
  }
  return {
    canvas,
    vertices: new Float32Array(vertices),
    groundVertexCount,
    ranges,
  };
}
